import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

export interface ThemeStore {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

/** Keep in sync with the inline bootstrap script in `public/index.html`. */
export const DEFAULT_THEME: Theme = 'light';

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  const isDark =
    theme === 'dark' ||
    (theme === 'system' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  root.classList.toggle('dark', isDark);
  // Keep native UI (scrollbars, form controls) aligned with the theme.
  root.style.colorScheme = isDark ? 'dark' : 'light';

  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', isDark ? '#0A1628' : '#F9FAFB');
  }
}

export const useThemeStore = create(
  persist<ThemeStore>(
    (set) => ({
      theme: DEFAULT_THEME,

      setTheme: (theme: Theme) => {
        set({ theme });
        applyTheme(theme);
      },
    }),
    {
      name: 'examing-theme',
      version: 1,
      migrate: (persistedState: any, version: number) => {
        if (!version || version < 1) {
          return {
            ...persistedState,
            theme: 'light',
          };
        }
        return persistedState as ThemeStore;
      },
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

// Apply persisted theme as soon as the store rehydrates
useThemeStore.persist.onFinishHydration((state) => {
  applyTheme(state.theme);
});

/** Call once in the root layout to follow OS preference changes while the theme is "system". */
export function initSystemThemeListener() {
  const mql = window.matchMedia('(prefers-color-scheme: dark)');

  const handler = () => {
    const { theme } = useThemeStore.getState();
    if (theme === 'system') applyTheme('system');
  };

  mql.addEventListener('change', handler);
  return () => mql.removeEventListener('change', handler);
}
